import React from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  FormCheck,
  InputGroup,
  Col,
  Tooltip,
  Row,
  OverlayTrigger,
  Modal,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validate, ValidationError } from 'class-validator';
import moment from 'moment';
import {
  Bloodwork,
  BloodworkRange,
  PanelField,
  PanelRange,
  User,
  Client,
  UserProfile,
  WhiteBloodCellDifferentialUnit,
  WhiteBloodCellDifferentialUnitType,
  MenstrualPhase,
  I3_SECTIONS,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { MathUtils } from '../../utils/MathUtils';
import Select from 'react-select';
import { GenericModal } from '../common/generic-modal';
import { TimeSavingFeaturesModal } from './time-saving-features-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LABCORP_MVX_PLUS_URL } from '../../models/MetabolicVulnerability';
import { Upload } from './upload';

const Spinner = require('react-spinkit');
// Served from the public/ folder (not bundled) so the app compiles even before
// the image file is added; the diagram lives at public/assets/imgs/menstrual-cycle-phases.jpg
const menstrualCycleDiagram =
  process.env.PUBLIC_URL + '/assets/imgs/menstrual-cycle-phases.jpg';

type Props = {
  hideEnterTestResults?: boolean;
  selectedClient?: Client;
  currentUser: User;
  currentUserProfile: UserProfile;
  currentClients: Client[];
  bloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  bloodworkEntryStore: BloodworkEntryStore;
  refreshBloodworks: () => void;
  loadClientBloodworks?: (clientId: string) => void;
  updateClient?: (client: Client) => Promise<Client>;
  history?: any;
  path?: any;
};

type State = {
  [key: string]: any;
  validationErrors: Map<string, string>;
  collectionDate: string;
  collectionDateForUI: Date;
  alerts: Array<any>;
  selectedClient: Client;
  clientId: string;
  disableSelectClient: boolean;
  showUpgradeModal: boolean;
  showUploadPdfUpgradeModal: boolean;
  showTimeSavingFeaturesModal: boolean;
  hideEnterTestResults: boolean;
  panelTitle: string;
};

function ClientOption(props: any) {
  const { data, isSelected, isFocused, innerRef, innerProps, options } = props;
  const index = (options as any[]).findIndex((o: any) => o.value === data.value);
  let bg = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
  if (isFocused) bg = '#e8f0fe';
  if (isSelected) bg = '#0d6efd';
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        backgroundColor: bg,
        color: isSelected ? '#ffffff' : '#212529',
        padding: '3px 12px',
        fontSize: '13px',
        borderBottom: '1px solid #eeeeee',
        cursor: 'pointer',
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {data.label}
    </div>
  );
}

export class BloodworkEntry extends React.Component<Props, State> {
  selectedClient: Client = this.props.selectedClient
    ? this.props.selectedClient
    : new Client();
  clientId: string = this.props.selectedClient
    ? this.props.selectedClient.id
    : undefined;

  state: State = {
    validationErrors: new Map<string, string>(),
    collectionDate: undefined,
    collectionDateForUI: undefined,
    alerts: [],
    selectedClient: this.selectedClient,
    clientId: this.clientId,
    disableSelectClient: false,
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    showUpgradeModal: false,
    showUploadPdfUpgradeModal: false,
    showTimeSavingFeaturesModal: false,
    showMenstrualPhaseInfo: false,
    hideEnterTestResults: this.props.hideEnterTestResults,
    panelTitle:
      this.props.bloodwork && this.props.bloodwork.isNew()
        ? 'Add Test Results'
        : 'Edit Test Results',
    bwUnits: this.selectedClient?.units || 'US',
    height: this.selectedClient?.height ?? '',
    weight: this.selectedClient?.weight ?? '',
    heightFt: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).ft)
      : '',
    heightIn: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).inches)
      : '',
  };

  async componentDidMount() {
    console.log('BloodworkEntry....componentDidMount.....................');
    // this.props.bloodworkEntryStore.setCurrentBloodwork(new Bloodwork());
    // Scroll-to-top must run on mount as well as componentWillReceiveProps:
    // when navigating in from the Home page Edit button, the bloodwork prop
    // is already set on the store before the route change, so props don't
    // change on mount and componentWillReceiveProps may never fire if all
    // container data is already cached.
    this.scrollToAddTestResultsTop();
    const showTimeSavingFeaturesModal =
      await this.displayTimeSavingFeaturesModal();
    if (showTimeSavingFeaturesModal) {
      this.setState({
        showTimeSavingFeaturesModal,
      });
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const bloodworkData: any = {};
    const { history, hideEnterTestResults } = nextProps;
    let bloodwork = new Bloodwork(nextProps.bloodwork);
    // Only scroll to the top when the record being edited actually changes
    // (a genuine "load this record into the form" navigation event). The
    // container is a MobX observer that re-renders as clients, ranges, the
    // user profile, and further-considerations finish loading; scrolling on
    // every one of those incidental prop updates yanks the user back to the
    // top repeatedly while the page is still settling.
    if (nextProps.bloodwork !== this.props.bloodwork) {
      this.scrollTop();
    }
    this.handleNumberOfTestsLimit();

    // setup needed if we are editing bloodwork
    if (bloodwork && !bloodwork.isNew()) {
      console.log('We have bloodwork and its not new.....');

      // Check to see if we came thru the "add" route
      if (this.isAddMode(history)) {
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = '';
        }
        this.setState({
          ...bloodworkData,
          hideEnterTestResults: false,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
        });
        return;
      }

      const collectionDateForUI = moment(bloodwork.collectionDate).toDate();
      bloodwork.convertWBCDifferentials(bloodwork.wbcDifferentialUnits);

      for (let key of Object.keys(bloodwork)) {
        bloodworkData[key] = bloodwork[key];
      }

      // Fall back to the client's profile values for height/weight/units
      // when the bloodwork itself doesn't have them. Prefer the live client
      // record over bloodwork.client, since the snapshot saved with the test
      // can predate height/weight being added to the client.
      const liveClient = bloodwork.clientId && nextProps.currentClients
        ? Client.findClient(bloodwork.clientId, nextProps.currentClients)
        : undefined;
      const bwClient = liveClient || bloodwork.client;
      const effectiveHeightCm = (bloodwork.height != null && bloodwork.height !== ('' as any))
        ? Number(bloodwork.height)
        : (bwClient?.height ?? null);
      const effectiveWeightLbs = (bloodwork.weight != null && bloodwork.weight !== ('' as any))
        ? Number(bloodwork.weight)
        : (bwClient?.weight ?? null);
      const effectiveUnits: 'US' | 'SI' = bwClient?.units === 'SI' ? 'SI' : 'US';
      const { ft: effFt, inches: effIn } = effectiveHeightCm != null
        ? MathUtils.cmToFtIn(effectiveHeightCm)
        : { ft: '', inches: '' };

      this.setState({
        ...bloodworkData,
        panelTitle: 'Edit Test Results',
        selectedClient: bwClient,
        disableSelectClient: true,
        hideEnterTestResults,
        collectionDateForUI: collectionDateForUI,
        bwUnits: effectiveUnits,
        height: effectiveHeightCm ?? '',
        weight: effectiveWeightLbs ?? '',
        heightFt: effectiveHeightCm != null ? String(effFt) : '',
        heightIn: effectiveHeightCm != null ? String(effIn) : '',
      });
    } else {
      console.log('inside else in componentWillReceiveProps....');
      const preselected = nextProps.selectedClient;
      // Only (re)hydrate the client-derived fields when the container actually
      // hands us a *different real* preselected client — i.e. navigating in
      // from the Clients table "Add Test Results" button (/bloodwork/add/:id).
      // On the plain /bloodwork/add route there is no preselected client, so
      // the container passes a fresh empty `new Client()` on every render. This
      // lifecycle runs on every incidental MobX re-render (clients, ranges,
      // profile, further-considerations finishing loading), so blindly resetting
      // selectedClient here would clobber a client the user just picked from the
      // dropdown — which hides the conditional menstrual-phase field for
      // premenopausal females.
      const preselectedId = preselected?.id;
      const preselectedChanged = preselectedId !== this.props.selectedClient?.id;
      if (preselectedId && preselectedChanged) {
        const heightCm = preselected?.height;
        const clientUnits = preselected?.units || 'US';
        const { ft, inches } = heightCm != null
          ? MathUtils.cmToFtIn(heightCm)
          : { ft: '', inches: '' };
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
          selectedClient: preselected,
          bwUnits: clientUnits,
          height: heightCm ?? '',
          weight: preselected?.weight ?? '',
          heightFt: heightCm != null ? String(ft) : '',
          heightIn: heightCm != null ? String(inches) : '',
        });
      } else {
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
        });
      }
    }
  }

  isAddMode(history: any) {
    return (
      history &&
      history.location &&
      history.location.pathname === '/bloodwork/add'
    );
  }

  /**
   * True when the form is editing an existing (already-saved) test rather than
   * adding a new one. Derived from props so it does NOT depend on
   * componentWillReceiveProps having fired — when navigating in from the Home
   * page Edit button the bloodwork prop is already set before the route change,
   * so that lifecycle (and the disableSelectClient state it sets) can be skipped
   * entirely. The client must never be changed on an existing test, so the
   * client dropdown is disabled whenever this is true.
   */
  private isEditingExistingTest(): boolean {
    const { bloodwork, history } = this.props;
    return (
      !!bloodwork &&
      typeof bloodwork.isNew === 'function' &&
      !bloodwork.isNew() &&
      !this.isAddMode(history)
    );
  }

  handleNumberOfTestsLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canAddTest()) {
      this.setState({ showUpgradeModal: true });
    }
  }

  handleUploadPdfLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canUploadPdf()) {
      this.setState({ showUploadPdfUpgradeModal: true });
    }
  }

  handleInputChange = (event: any) => {
    // console.log(event ? event.target : 'event was falsey...');
    const fieldId = event.target.name;
    let value = event.target.value;

    // Convert empty string to undefined so our automatic calculations work correctly
    if (value.trim().length === 0) {
      value = undefined;
    }

    // If the wbcDifferentialUnits selection changes then clear out wbc differencial inputs
    if (fieldId === 'wbcDifferentialUnits') {
      this.setState({
        [fieldId]: value,
        neutrophils: '',
        lymphocytes: '',
        monocytes: '',
        eosinophils: '',
        basophils: '',
      });
    } else {
      this.setState({
        [fieldId]: value,
      });
    }

    if (fieldId === 'clientId') {
      this.handleClientChange(event.target.value);
    }

    this.clearAlerts();
  };

  handleClientChange = (clientId: string) => {
    const client = Client.findClient(clientId, this.props.currentClients);
    const heightCm = client?.height;
    const clientUnits = client?.units || 'US';
    const { ft, inches } = heightCm != null
      ? MathUtils.cmToFtIn(heightCm)
      : { ft: '', inches: '' };
    this.setState({
      selectedClient: client || new Client(),
      bwUnits: clientUnits,
      height: heightCm ?? '',
      weight: client?.weight ?? '',
      heightFt: heightCm != null ? String(ft) : '',
      heightIn: heightCm != null ? String(inches) : '',
    });
  };

  handleOnBlur = (event: any) => {
    this.handleCalculatedRatioFields(event.target.name);
    this.handleGlobulinCalculation(event.target.name);
    this.handleMeanCorpuscularVolumeCalculation(event.target.name);
    this.handleMeanCorpuscularHGBCalculation(event.target.name);
    this.handleMCHConcentrationCalculation(event.target.name);
    this.handleBilirubinCalculation(event.target.name);
    this.handleSaturationCalculation(event.target.name);
    this.handleTestosteroneBioavailableCalculation(event.target.name);
    this.handleLessThanCalculation(event.target.name);
  };

  // Each ratio gets its own `if`, deliberately not an else-if chain: HDL is a
  // component of BOTH Cholesterol/HDL and Triglycerides/HDL, so a chain stops at
  // the first match and leaves the second ratio stale. That bit TG/HDL — it only
  // repopulated when Triglycerides was blurred, so entering Triglycerides before
  // HDL left it blank. TG/HDL is one of the three primary Glucose markers, so a
  // blank there silently shifts the Glucose gauge and pattern.
  handleCalculatedRatioFields(fieldId: string) {
    if (fieldId === 'bun' || fieldId === 'creatinine') {
      this.handlePopulateRatio('bun', 'creatinine', 'bunCreatinineRatio');
    }
    if (fieldId === 'albumin' || fieldId === 'globulin') {
      this.handlePopulateRatio('albumin', 'globulin', 'albuminGlobulinRatio');
    }
    if (fieldId === 'cholesterol' || fieldId === 'hdl') {
      this.handlePopulateRatio('cholesterol', 'hdl', 'cholesterolHDLRatio');
    }
    if (fieldId === 'triglycerides' || fieldId === 'hdl') {
      this.handlePopulateRatio('triglycerides', 'hdl', 'triglyceridesHDLRatio');
    }
    if (fieldId === 'apolipoproteinB' || fieldId === 'apolipoproteinA1') {
      this.handlePopulateRatio(
        'apolipoproteinB',
        'apolipoproteinA1',
        'apoBapoA1Ratio',
      );
    }
  }

  handlePopulateRatio(
    numerator: string,
    denominator: string,
    ratioField: string,
  ) {
    // Number('') is 0, not NaN. Without an explicit blank check a missing
    // component yields a real-looking ratio — 0 when the numerator is blank
    // (and 0 reads as *optimal* for TG/HDL), or a divide-by-zero when the
    // denominator is. A ratio the lab never reported must stay empty.
    const isMeasured = (raw: any) =>
      raw != null && String(raw).trim() !== '' && !isNaN(Number(raw));

    const rawNumerator = this.state[numerator];
    const rawDenominator = this.state[denominator];

    if (
      !isMeasured(rawNumerator) ||
      !isMeasured(rawDenominator) ||
      Number(rawDenominator) === 0
    ) {
      // Clear rather than leave the previous value behind: a stale ratio
      // computed from the *old* HDL is worse than a visibly empty field.
      this.setState({ [ratioField]: '' });
      return;
    }

    const ratio = MathUtils.round(
      Number(rawNumerator) / Number(rawDenominator),
      2,
    );
    this.setState({ [ratioField]: isNaN(ratio) ? '' : ratio });
  }

  handleGlobulinCalculation(fieldId: string) {
    if (fieldId === 'protein' || fieldId === 'albumin') {
      const protein = Number(this.state.protein);
      const albumin = Number(this.state.albumin);
      if (!isNaN(protein) && !isNaN(albumin)) {
        const globulin = protein - albumin;
        this.setState({
          globulin: MathUtils.round(globulin, 2),
        });
      }
    }
  }

  handleMeanCorpuscularVolumeCalculation(fieldId: string) {
    if (fieldId === 'hematocrit' || fieldId === 'redBloodCells') {
      const hct = Number(this.state.hematocrit);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hct) && !isNaN(rbc)) {
        //  MCV = HCT x 10 / RBC
        const mcv = (hct * 10) / rbc;
        this.setState({
          meanCorpuscularVolume: MathUtils.round(mcv, 2),
        });
      }
    }
  }

  handleMeanCorpuscularHGBCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'redBloodCells') {
      const hgb = Number(this.state.hemoglobin);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hgb) && !isNaN(rbc)) {
        // MCH = HGB x 10 / RBC
        const mch = (hgb * 10) / rbc;
        this.setState({
          meanCorpuscularHGB: MathUtils.round(mch, 2),
        });
      }
    }
  }

  handleMCHConcentrationCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'hematocrit') {
      const hgb = Number(this.state.hemoglobin);
      const hct = Number(this.state.hematocrit);
      if (!isNaN(hgb) && !isNaN(hct)) {
        // MCHC = HGB x 100 / HCT
        const mchc = (hgb * 100) / hct;
        this.setState({
          mchConcentration: MathUtils.round(mchc, 2),
        });
      }
    }
  }

  handleBilirubinCalculation(fieldId: string) {
    // total = direct + indirect
    // direct = total - indirect
    // indirect = total - direct
    if (
      fieldId === 'bilirubin' ||
      fieldId === 'bilirubinDirect' ||
      fieldId === 'bilirubinIndirect'
    ) {
      const total = Number(
        this.state.bilirubin === '' ? undefined : this.state.bilirubin,
      );
      let direct = Number(
        this.state.bilirubinDirect === ''
          ? undefined
          : this.state.bilirubinDirect,
      );
      let indirect = Number(
        this.state.bilirubinIndirect === ''
          ? undefined
          : this.state.bilirubinIndirect,
      );
      if (!isNaN(total) && !isNaN(direct) && isNaN(indirect)) {
        indirect = total - direct;
        this.setState({
          bilirubinIndirect: MathUtils.round(indirect, 2),
        });
      } else if (!isNaN(total) && !isNaN(indirect) && isNaN(direct)) {
        direct = total - indirect;
        this.setState({
          bilirubinDirect: MathUtils.round(direct, 2),
        });
      }
    }
  }

  handleSaturationCalculation(fieldId: string) {
    if (fieldId === 'iron' || fieldId === 'tibc') {
      const iron = Number(this.state.iron);
      const tibc = Number(this.state.tibc);
      if (!isNaN(iron) && !isNaN(tibc)) {
        //  % saturation = ( iron / TIBC ) * 100
        const saturation = (iron / tibc) * 100;
        this.setState({
          saturation: MathUtils.round(saturation, 1),
        });
      }
    }
  }

  handleTestosteroneBioavailableCalculation(fieldId: string) {
    if (
      fieldId === 'testosteroneBioavailable' ||
      fieldId === 'testosteroneTotal'
    ) {
      const bioavailable = Number(this.state.testosteroneBioavailable);
      const total = Number(this.state.testosteroneTotal);
      if (!isNaN(bioavailable) && !isNaN(total)) {
        const bioavailablePercentage = (bioavailable / total) * 100;
        this.setState({
          testosteroneBioavailablePercent: MathUtils.round(
            bioavailablePercentage,
            1,
          ),
        });
      }
    }
  }

  calculateEGFRFromState(): number | null {
    const creatinine = this.state.creatinine != null && this.state.creatinine !== ''
      ? Number(this.state.creatinine) : null;
    const client: Client = this.state.selectedClient;

    if (!creatinine || !client?.dateOfBirth || !client?.gender) return null;

    const collectionDate = this.state.collectionDate;
    if (!collectionDate) return null;

    const age = client.ageAsOf(collectionDate);
    if (!age || age <= 0) return null;

    if (age < 18) {
      const heightCm = (this.state.height != null && this.state.height !== '')
        ? Number(this.state.height)
        : client.height;
      if (!heightCm) return null;
      return Math.round((0.413 * heightCm) / creatinine * 10) / 10;
    }

    // CKD-EPI 2021 (race-free) for adults ≥18
    const isFemale = client.isFemale();
    const kappa = isFemale ? 0.7 : 0.9;
    const alpha = isFemale ? -0.241 : -0.302;
    const sexFactor = isFemale ? 1.012 : 1.0;
    const ratio = creatinine / kappa;
    return Math.round(
      142 *
        Math.pow(Math.min(ratio, 1), alpha) *
        Math.pow(Math.max(ratio, 1), -1.2) *
        Math.pow(0.9938, age) *
        sexFactor *
        10,
    ) / 10;
  }

  handleLessThanCalculation(fieldId: string) {
    if (fieldId === 'tpoAb' && this.state.tpoAb === '<9') {
      this.setState({ tpoAb: 8 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<12') {
      this.setState({ tpoAb: 11.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<28') {
      this.setState({ tpoAb: 27.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<0.3') {
      this.setState({ tpoAb: 0.2 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<1') {
      this.setState({ tpoAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<1', '<1.0', '≤1'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<1.5', '<1.50', '≤1.5'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 1.4 });
    }

    if (fieldId === 'tgAb' && ['<15', '<15.000'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 14.99 });
    }

    if (fieldId === 'tgAb' && ['>2250', '>2250.0'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 2251 });
    }

    if (fieldId === 'estroneE1' && this.state.estroneE1 === '<6') {
      this.setState({ estroneE1: 5.9 });
    }

    if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.10') {
      this.setState({ estriolE3: 0.09 });
    }

        if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.2') {
      this.setState({ estriolE3: 0.19 });
    }

    if (fieldId === 'bilirubin' && this.state.bilirubin === '<0.2') {
      this.setState({ bilirubin: 0.19 });
    }

    if (
      fieldId === 'bilirubinDirect' && this.state.bilirubinDirect === '<0.2') {
      this.setState({ bilirubinDirect: 0.19 });
    }

    if (fieldId === 'insulin' && this.state.insulin === '<3') {
      this.setState({ insuln: 2.9 });
    }

    if (
      fieldId === 'testosteroneFreeDirect' && this.state.testosteroneFreeDirect === '<0.2') {
      this.setState({ testosteroneFreeDirect: 0.19 });
    }

    if (
      fieldId === 'testosteroneTotal' && this.state.testosteroneTotal === '<2.5') {
      this.setState({ testosteroneTotal: 2.4 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<8.4') {
      this.setState({ lipoproteinA: 8.3 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<10') {
      this.setState({ lipoproteinA: 9.9 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<19.6') {
      this.setState({ lipoproteinA: 19.5 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>20.0') {
      this.setState({ folateSerum: 21 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>24.0') {
      this.setState({ folateSerum: 25 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1396') {
      this.setState({ folateRBC: 1397 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1325') {
      this.setState({ folateRBC: 1326 });
    }

    if (fieldId === 'bileAcids' && this.state.bileAcids === '<1.0') {
      this.setState({ bileAcids: 0.9 });
    }

    if (fieldId === 'ldl' && this.state.ldl === '<100') {
      this.setState({ ldl: 99 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<15') {
      this.setState({ estradiolE2: 14.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<10') {
      this.setState({ estradiolE2: 9.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<5.0') {
      this.setState({ estradiolE2: 4.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<30') {
      this.setState({ estradiolE2: 29.9 });
    }

    if (fieldId === 'pregnenolone' && this.state.pregnenolone === '<5') {
      this.setState({ pregnenolone: 4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.5') {
      this.setState({ progesterone: 0.4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.1') {
      this.setState({ progesterone: 0.09 });
    }
    
    if (fieldId === 'progesterone' && this.state.progesterone === '<0.05') {
      this.setState({ progesterone: 0.04 });
    }

    if (fieldId === 'lh' && this.state.lh === '<0.05') {
      this.setState({ lh: 0.04 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.2') {
      this.setState({ hsCRP: 0.1 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.15') {
      this.setState({ hsCRP: 0.14 });
    }

    if (fieldId === 'eGFR' && (this.state.eGFR === '>90' || this.state.eGFR === '>60')) {
      const fallback = this.state.eGFR === '>90' ? 91 : 61;
      this.setState({ eGFR: this.calculateEGFRFromState() ?? fallback });
    }

    if (fieldId === 'eGFRforAA' && (this.state.eGFRforAA === '>90' || this.state.eGFRforAA === '>60')) {
      const fallback = this.state.eGFRforAA === '>90' ? 91 : 61;
      this.setState({ eGFRforAA: this.calculateEGFRFromState() ?? fallback });
    }

    if (fieldId === 'dDimer' && this.state.dDimer === '<0.20') {
      this.setState({ dDimer: 0.1 });
    }

    if (fieldId === 'psaTotal' && this.state.psaTotal === '<0.01') {
      this.setState({ psaTotal: 0.009 });
    }
    if (fieldId === 'psaFree' && this.state.psaFree === '<0.01') {
      this.setState({ psaFree: 0.009 });
    }

  }

  handleCollectionDateChange = (value: any) => {
    const startOfDay = moment(value).startOf('day').toISOString();
    this.setState({
      collectionDate: startOfDay,
      collectionDateForUI: value,
    });
    this.clearAlerts();
  };

  handleSubmit = (event: any) => {
    event.preventDefault();

    if (!this.props.currentUserProfile.canAddTest()) {
      this.handleNumberOfTestsLimit();
      return;
    }

    let validationErrors = new Map<string, string>();
    let bloodwork: Bloodwork = new Bloodwork();
    bloodwork.practitionerId = this.props.currentUser.subject;

    for (let key of Object.keys(this.state)) {
      if (
        key !== 'validationErrors' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'collectionDateForUI' &&
        key !== 'hideEnterTestResults' &&
        key !== 'panelTitle' &&
        key !== 'disableSelectClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal' &&
        key !== 'showMenstrualPhaseInfo' &&
        key !== 'heightFt' &&
        key !== 'heightIn' &&
        key !== 'bwUnits'
      ) {
        if (this.state[key] === '') {
          // Remove numeric attributes that have empty string
          // DynamoDB will NOT accept empty string....Stupid, right!
          // nulls will be given the value of true....weirdness!
          bloodwork[key] = undefined;
        } else {
          const nbr: number = Number(this.state[key]);
          if (isNaN(nbr)) {
            bloodwork[key] = this.state[key];
          } else {
            bloodwork[key] = nbr;
          }
        }
      }
    }
    // console.log(bloodwork);
    this.validate(bloodwork, validationErrors);
  };

  validate(bloodwork: Bloodwork, validationErrors: Map<string, string>) {
    // console.log(bloodwork);

    // Special check for bilirubin
    if (!bloodwork.isBilirubinValid()) {
      // Use a fake key here so the messages doesn't have the field name in it
      validationErrors.set(
        'fakeBilirubinKey1',
        'Bilirubin, Direct and Bilirubin, Indirect must not be greater than Bilirubin, Total.',
      );
      validationErrors.set(
        'fakeBilirubinKey2',
        'Click the Clear Bilirubin button next to Bilirubin, Total.',
      );
    }

    // Special check for collection date and client DOB
    if (bloodwork.collectionDate && bloodwork.clientId) {
      const { currentClients } = this.props;
      const client = Client.findClient(bloodwork.clientId, currentClients);

      if (!client.isBornOnOrBefore(bloodwork.collectionDate)) {
        validationErrors.set(
          'fakeCollectionDate',
          "Collection Date must be the same or after the client's Date of Birth.",
        );
      }
    }

    // Special check for MPV (Mean Platelet Volume) max value
    if (bloodwork.meanPlateletVolume && bloodwork.meanPlateletVolume > 20) {
      validationErrors.set(
        'meanPlateletVolume',
        'must not be greater than 20 fL.',
      );
    }

    // I³ section scores must be within 0..max for their section.
    for (const s of I3_SECTIONS) {
      const value = (bloodwork as any)[s.id];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      if (value < 0 || value > s.max) {
        validationErrors.set(s.id, `must be between 0 and ${s.max}.`);
      }
    }

    // Special check to see if menstrualPhase is required and if we should clear it
    if (this.isMenstrualPhaseRequired() && !bloodwork.menstrualPhase) {
      validationErrors.set(
        'menstrualPhase',
        'is required for premenopausal female clients when a sex hormone marker (Estradiol, FSH, LH, Progesterone, etc.) is provided.',
      );
    } else if (!this.showMenstrualPhase()) {
      // If we arent supposed to show it then clear it
      bloodwork.menstrualPhase = undefined;
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors,
      });

      this.scrollToAddTestResults();
      return;
    }

    validate(bloodwork).then(errors => {
      // errors is an array of validation errors
      if (errors.length > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }

        this.setState({
          validationErrors: validationErrors,
        });

        this.scrollToAddTestResults();
      } else {
        this.setState({
          validationErrors: new Map<string, string>(),
        });
        // Set WBC Differentials if not entered as percentages
        const wbcDifferentialUnits = parseInt(
          this.state.wbcDifferentialUnits,
          10,
        );
        bloodwork.convertWBCDifferentialsIfNecessary(wbcDifferentialUnits);
        // console.log('bloodwork:', bloodwork);
        this.saveBloodwork(bloodwork);
      }
    });
  }

  saveBloodwork(bloodwork: Bloodwork) {
    const {
      refreshBloodworks,
      loadClientBloodworks,
      bloodworkEntryStore,
      currentClients,
      updateClient,
    } = this.props;

    // Populate the embedded client snapshot for a new test. Clone the live
    // client so stamping height/weight/units onto the snapshot below does not
    // mutate the live record before the change-detection sync block runs (that
    // block compares against the live client to decide whether to persist it).
    if (bloodwork.isNew()) {
      const liveClient = Client.findClient(bloodwork.clientId, currentClients);
      bloodwork.client = liveClient ? new Client(liveClient) : new Client();
    }

    // The height/weight/units entered on a test are the client's measurements
    // as of that test, so the embedded snapshot must carry them. Every BMI /
    // Client-Information readout (analysis screen + PDF) reads bloodwork.client,
    // so without this the display reverts to the client's prior profile value
    // instead of what was just entered. Mirrors saveClientInfo in
    // bloodwork-test-analysis.tsx. Height is cm, weight is lbs (canonical).
    if (bloodwork.client) {
      bloodwork.client.height = bloodwork.height as number | undefined;
      bloodwork.client.weight = bloodwork.weight as number | undefined;
      bloodwork.client.units = this.state.bwUnits === 'SI' ? 'SI' : 'US';
    }

    bloodworkEntryStore
      .saveBloodwork(bloodwork)
      .then(savedBloodwork => {
        // console.log(savedBloodwork);
        this.addAlert(
          { type: 'success', text: 'Successfully saved test result!' },
          false,
          5000,
        );
        // Load the client bloodworks since we could be editing a bloodwork on the analyze bloodworks page
        if (loadClientBloodworks && bloodwork.clientId) {
          loadClientBloodworks(bloodwork.clientId);
        }
        refreshBloodworks();

        // Sync height/weight/units back to the Client record if they changed
        if (updateClient && bloodwork.clientId) {
          const client = Client.findClient(bloodwork.clientId, currentClients);
          if (client) {
            const newHeight = bloodwork.height as number | undefined;
            const newWeight = bloodwork.weight as number | undefined;
            const newUnits: 'US' | 'SI' = this.state.bwUnits === 'SI' ? 'SI' : 'US';
            // Treat undefined client.units as 'US' (the form default) to avoid spurious saves
            const effectiveClientUnits: 'US' | 'SI' = client.units === 'SI' ? 'SI' : 'US';
            if (client.height !== newHeight || client.weight !== newWeight || effectiveClientUnits !== newUnits) {
              client.height = newHeight;
              client.weight = newWeight;
              client.units = newUnits;
              updateClient(client).catch(err =>
                console.error('Failed to update client height/weight/units:', err),
              );
            }
          }
        }

        this.clearForm(false);
        this.handleNumberOfTestsLimit();
        bloodworkEntryStore.resetCurrentBloodwork();
        bloodworkEntryStore.setHideEnterTestResults(true);

        // If we arrived from Manage Test Results, return there with the
        // client preselected so the user lands back where they started.
        const history = this.props.history;
        const navState = history && history.location && history.location.state;
        if (navState && navState.returnTo && history) {
          history.push({
            pathname: navState.returnTo,
            state: { clientId: navState.clientId || bloodwork.clientId },
          });
        }
      })
      .catch(error => {
        console.error(error);
        this.addAlert({ type: 'danger', text: 'Failed to add test result.' });
      });
  }

  clearForm = (clearAlerts?: boolean) => {
    const isAddMode = this.isAddMode(this.props.history);
    const bloodwork = {};
    Object.keys(this.state).forEach(key => {
      if (
        key !== 'validationErrors' &&
        key !== 'collectionDate' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal'
      ) {
        bloodwork[key] = '';
      }
    });

    this.setState({
      ...bloodwork,
      panelTitle: this.state.panelTitle,
      validationErrors: new Map<string, string>(),
      collectionDate: undefined,
      collectionDateForUI: undefined,
      // When editing a client's test, keep the client selected — it's the
      // record being edited and the client selector is disabled in edit mode.
      selectedClient: isAddMode ? new Client() : this.state.selectedClient,
      clientId: isAddMode ? '' : this.state.clientId,
      wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    });

    if (clearAlerts) {
      this.clearAlerts();
    }
  };

  clearAlerts = () => {
    this.setState({
      alerts: [],
    });
  };

  displayErrorMessage(
    error: ValidationError,
    validationErrors: Map<string, string>,
  ) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        const fieldLabel = this.findFieldLabel(fieldName);
        listItems.push(
          <li key={fieldName + '-error'}>
            {fieldLabel} {validationErrors.get(fieldName)}
          </li>,
        );
      }
    });

    return listItems;
  }

  findFieldLabel(attribute: string): string {
    const panelField: PanelField = PanelField.ALL_FIELDS.find(field => {
      return field.id === attribute;
    });
    return panelField ? panelField.label : '';
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>{this.renderErrorListItems(validationErrors)}</ul>
        </div>
      </div>
    );
  }

  renderAlerts() {
    // console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert, index);
    });

    return <div>{alerts}</div>;
  }

  renderAlert(alert: any, index: number) {
    return (
      <Alert key={index} variant={alert.type} dismissible>
        {alert.text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? (
      this.renderValidationErrors(validationErrors)
    ) : (
      <div />
    );
  }

  addAlert = (
    alert: any,
    keepCurrentAlerts: boolean = false,
    autoClose?: number,
  ) => {
    // console.log('add alert called....');
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts,
    });

    if (autoClose) {
      setTimeout(() => {
        this.clearAlerts();
      }, autoClose);
    }
  };

  createFieldElements = (field: PanelField) => {
    if (field.id === 'collectionDate') {
      return this.collectionDate(field);
    }
    if (field.id === 'clientId') {
      return this.selectClient(field);
    }
    if (field.id === 'wbcDifferentialUnits') {
      return this.wbcDifferentialUnits(field);
    }
    if (field.id === 'menstrualPhase') {
      return this.menstrualPhase(field);
    }
    return this.input(field);
  };

  collectionDate(field: PanelField) {
    return (
      // TODO: Look into using a customInput here so that validation styling is applied.
      <DatePicker
        selected={this.state.collectionDateForUI}
        onChange={this.handleCollectionDateChange}
        maxDate={new Date()}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        todayButton
        dropdownMode="select"
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        customInput={<Form.Control style={{ fontSize: '14px' }} />}
        popperPlacement="bottom"
        popperModifiers={{
          flip: {
            behavior: ['bottom'], // don't allow it to flip to be above
          },
          preventOverflow: {
            enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
          },
          hide: {
            enabled: false, // turn off since needs preventOverflow to be enabled
          },
        }}
      />
    );
  }

  selectClient(field: PanelField) {
    console.log(
      'rendering selectClient......disableSelectClient: ' +
        this.state.disableSelectClient,
    );
    const { currentClients } = this.props;

    if (currentClients === undefined) {
      return <Spinner name="ball-clip-rotate" color="#337ab7" fadeIn="none" />;
    }

    if (currentClients.length === 0) {
      return (
        <LinkContainer exact to="/client/add">
          <Button>Add Client</Button>
        </LinkContainer>
      );
    }

    const sorted = currentClients
      .slice()
      .sort((a: Client, b: Client) =>
        (a.lastName || '').localeCompare(b.lastName || ''),
      );

    const options = sorted.map((c: Client) => ({
      value: c.id,
      label: `${c.lastName}, ${c.firstName}`,
    }));

    const selectedValue =
      options.find((o) => o.value === this.state[field.id]) ?? null;
    const hasError = !!this.state.validationErrors.get(field.id);

    return (
      <Select
        placeholder="Search by name..."
        isSearchable
        // The client cannot be changed on an existing test. Derive this from
        // props (isEditingExistingTest) instead of the disableSelectClient
        // state flag: that flag is only set in componentWillReceiveProps (which
        // can be skipped when editing opens from the Home page Edit button) and
        // is never reset to false, so it would also wrongly stay disabled if an
        // Add form is opened in the same component instance after an edit.
        isDisabled={this.isEditingExistingTest()}
        options={options}
        value={selectedValue}
        onChange={(opt: any) => {
          const rawValue: string = opt?.value ?? '';
          this.setState({ [field.id]: rawValue || undefined });
          this.handleClientChange(rawValue);
          this.clearAlerts();
        }}
        maxMenuHeight={500}
        components={{ Option: ClientOption }}
        styles={{
          control: (base: any, state: any) => ({
            ...base,
            borderColor: hasError
              ? '#dc3545'
              : state.isFocused
              ? '#80bdff'
              : '#ced4da',
            boxShadow: hasError
              ? '0 0 0 0.2rem rgba(220,53,69,.25)'
              : state.isFocused
              ? '0 0 0 0.2rem rgba(0,123,255,.25)'
              : 'none',
            '&:hover': { borderColor: hasError ? '#dc3545' : '#ced4da' },
            fontSize: '14px',
            minHeight: '38px',
          }),
          menu: (base: any) => ({
            ...base,
            zIndex: 9999,
          }),
          placeholder: (base: any) => ({
            ...base,
            color: '#6c757d',
            fontSize: '14px',
          }),
          singleValue: (base: any) => ({
            ...base,
            fontSize: '12px',
            fontWeight: 600,
          }),
        }}
      />
    );
  }

  wbcDifferentialUnits(field: PanelField) {
    let wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    // Make sure we default a value if none present
    if (!wbcDifferentialUnits) {
      wbcDifferentialUnits = WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS;
    }
    return (
      <InputGroup>
        <FormCheck
          inline
          id="unit2"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_KCELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          inline
          id="unit3"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_CELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          id="unit1"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.PERCENT}
          inline
          label={WhiteBloodCellDifferentialUnit.PERCENT}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT
          }
        ></FormCheck>
      </InputGroup>
    );
  }

  menstrualPhase(field: PanelField) {
    return (
      <Form.Control
        as="select"
        name="menstrualPhase"
        value={this.state.menstrualPhase}
        onChange={this.handleInputChange}
        isInvalid={!!this.state.validationErrors.get(field.id)}
      >
        <option value="">{field.placeholder}</option>
        <option value={MenstrualPhase.FOLLICULAR}>
          {MenstrualPhase.FOLLICULAR}
        </option>
        <option value={MenstrualPhase.OVULATION}>
          {MenstrualPhase.OVULATION}
        </option>
        <option value={MenstrualPhase.LUTEAL}>{MenstrualPhase.LUTEAL}</option>
        <option value={MenstrualPhase.NONE}>
          {MenstrualPhase.NONE} (children's ranges)
        </option>
      </Form.Control>
    );
  }

  input(field: PanelField) {
    const { validationErrors } = this.state;

    return (
      <InputGroup>
        <Form.Control
          name={field.id}
          onChange={this.handleInputChange}
          onBlur={this.handleOnBlur}
          placeholder={this.placeholder(field)}
          type="text"
          value={this.state[field.id]}
          isInvalid={!!validationErrors.get(field.id)}
        />
        {this.units(field)}
      </InputGroup>
    );
  }

  units(field: PanelField) {
    let units = field.units;
    const wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    if (field.isWBCDifferential) {
      units = field.wbcUnits(wbcDifferentialUnits);
    }
    return units ? (
      <InputGroup.Append>
        <InputGroup.Text>{units}</InputGroup.Text>
      </InputGroup.Append>
    ) : (
      ''
    );
  }

  placeholder(field: PanelField): string {
    if (field.isWBCDifferential) {
      const wbcDifferentialUnits = parseInt(
        this.state.wbcDifferentialUnits,
        10,
      );
      return field.wbcPlaceholder(wbcDifferentialUnits);
    } else if (field.placeholder) {
      return field.placeholder;
    } else {
      const { bloodworkRanges } = this.props;
      const { selectedClient, collectionDate, menstrualPhase } = this.state;
      const bloodwork = new Bloodwork({
        client: selectedClient,
        collectionDate: collectionDate || moment().startOf('day').toISOString(),
        menstrualPhase,
      });

      const range = BloodworkRange.findRange(
        bloodworkRanges,
        field.id,
        bloodwork,
      );

      if (range) {
        // console.log('placeholder.................................');
        // console.log(range);
        return range.placeholder(field.getPlaceholderPrecision());
      }
    }
    return undefined;
  }

  clearBilirubin() {
    this.setState({
      bilirubin: '',
      bilirubinDirect: '',
      bilirubinIndirect: '',
    });
  }

  addAdditionalInfo(field: PanelField) {
    if (field.id === 'bilirubin' || field.id === 'bilirubinDirect') {
      const tooltip = (
        <Tooltip id="bilirubin-tooltip">
          <div>
            Click this button to clear the values for Bilirubin, Total, Direct,
            and Indirect if a mistake was made in entering Bilirubin, Direct or
            Indirect.
          </div>
          <div>
            Re-enter the value for Bilirubin, Total first, then enter a value
            for either Direct or Indirect and the other will be automatically
            calculated.
          </div>
        </Tooltip>
      );
      return (
        <OverlayTrigger overlay={tooltip}>
          <Button
            type="button"
            onClick={() => {
              this.clearBilirubin();
            }}
          >
            Clear Bilirubin
          </Button>
        </OverlayTrigger>
      );
    }
    return null;
  }

  isMenstrualPhaseRequired() {
    // Any marker whose ranges depend on the menstrual phase requires one to be
    // selected — not just Estradiol. Check every phase-dependent marker so
    // saving FSH/LH/Progesterone/etc. without a phase is also blocked.
    const hasPhaseDependentMarker = PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.some(
      attribute => {
        const value = this.state[attribute];
        return value !== undefined && value !== null && value !== '';
      },
    );
    return hasPhaseDependentMarker && this.showMenstrualPhase();
  }

  showMenstrualPhase() {
    const { selectedClient, collectionDate } = this.state;
    if (selectedClient && collectionDate) {
      const age = selectedClient.ageAsOf(collectionDate);
      return selectedClient.isPremenopausal() && age > 10;
    }
    return false;
  }

  renderMenstrualPhaseInfoIcon() {
    const tooltip = (
      <Tooltip id="menstrual-phase-info-tooltip">
        Click to view the phases of the menstrual cycle.
      </Tooltip>
    );
    return (
      <OverlayTrigger overlay={tooltip}>
        <FontAwesomeIcon
          icon="info-circle"
          style={{ marginLeft: '6px', cursor: 'pointer', color: '#337ab7' }}
          onClick={() => this.setState({ showMenstrualPhaseInfo: true })}
        />
      </OverlayTrigger>
    );
  }

  formGroup(field: PanelField) {
    const className = field.isFormCheck ? 'labsmarts-form-check-label' : '';
    // Only show menstrual phase if necessary
    if (field.id === 'menstrualPhase' && !this.showMenstrualPhase()) {
      return null;
    }
    return (
      <Form.Group
        as={Row}
        key={field.id + '-group'}
        controlId={field.id + '-group'}
      >
        <Form.Label column sm className={className}>
          {field.label}
          {field.id === 'menstrualPhase' &&
            process.env.REACT_APP_ENV !== 'prod' &&
            process.env.REACT_APP_ENV !== 'slava' &&
            this.renderMenstrualPhaseInfoIcon()}
        </Form.Label>
        <Col sm>{this.createFieldElements(field)}</Col>
        <Col sm>{this.addAdditionalInfo(field)}</Col>
      </Form.Group>
    );
  }

  // A yes/no toggle for the I³ section, persisted as a 0/1 number so it flows
  // through the same save pipeline as the numeric fields.
  i3ToggleGroup(id: string, label: string) {
    const checked = !!Number(this.state[id]);
    return (
      <Form.Group as={Row} key={id + '-group'} controlId={id + '-group'}>
        <Form.Label column sm>
          {label}
        </Form.Label>
        <Col sm>
          <Form.Check
            type="checkbox"
            id={id}
            name={id}
            label="Yes"
            checked={checked}
            onChange={(e: any) =>
              this.setState({ [id]: e.target.checked ? 1 : 0 })
            }
          />
        </Col>
        <Col sm />
      </Form.Group>
    );
  }

  // Add Test Results card for the Immune & Inflammatory Burden Index (I³).
  // Env-gated to local dev only (hidden in slava + prod), matching the report sections.
  renderImmuneInflammatoryCard() {
    return (
      <Card>
        <Card.Header>Immune &amp; Inflammatory Burden Index (I³)</Card.Header>
        <Card.Body>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
            Enter each section's questionnaire subtotal (0 to the shown maximum).
            Weighted scores, the total Weighted I³ Score, and the Root Cause
            triggers are calculated automatically.
          </p>
          <Form noValidate>
            {PanelField.IMMUNE_INFLAMMATORY_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
            <hr />
            <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
              Toxins and Trauma share the same questionnaire sections. These
              optional flags help separate them.
            </p>
            {this.i3ToggleGroup('i3ToxinExposure', 'Known toxin / mold exposure')}
            {this.i3ToggleGroup('i3TraumaHistory', 'Trauma / ACE / chronic-stress history')}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  // Add Test Results card for Metabolic Vulnerability (MVX Plus). Env-gated to
  // local dev only (hidden in slava + prod), matching the report section.
  renderMvxCard() {
    return (
      <Card>
        <Card.Header>
          Metabolic Vulnerability Index (MVX) Plus
          <OverlayTrigger
            overlay={<Tooltip id="mvx-labcorp-tooltip">View the Labcorp MVX Plus test page (#123601)</Tooltip>}
          >
            <a
              href={LABCORP_MVX_PLUS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="external-link"
              style={{ marginLeft: '8px' }}
            >
              <FontAwesomeIcon icon="external-link-alt" />
            </a>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
            Enter the three index scores and six factor values from the Labcorp
            MVX Plus report. Zones, sub-indices, and patterns are calculated
            automatically.
          </p>
          <Form noValidate>
            {PanelField.MVX_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  scrollToAddTestResults() {
    const element = document.getElementById('add-test-results-panel');
    if (element) {
      element.scrollIntoView();
    }
  }

  showTimeSavingFeaturesModal = () => {
    this.setState({
      showTimeSavingFeaturesModal: true,
    });
  };

  async displayTimeSavingFeaturesModal(): Promise<boolean> {
    const { currentUserProfile } = this.props;
    if (currentUserProfile) {
      const hasSeenTimeSavingFeaturesModal = await localStorage.getItem(
        `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      );
      return hasSeenTimeSavingFeaturesModal !== 'true';
    } else {
      return Promise.resolve(false);
    }
  }

  hasSeenTimeSavingFeaturesModal = (): void => {
    // console.log('hasSeenTimeSavingFeaturesModal invoked....');
    const { currentUserProfile } = this.props;
    localStorage.setItem(
      `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      'true',
    );
    this.setState({
      showTimeSavingFeaturesModal: false,
    });
  };

  scrollTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  // Lands the top of the Add Test Results panel just below the sticky header.
  // React Router (v5) preserves the previous page's scroll offset across a push
  // navigation, so arriving here from a long list (e.g. the Manage Clients
  // table, scrolled well down) inherits that offset and leaves the user partway
  // into the form, around the BMI row above CBC. A single synchronous reset on
  // mount doesn't stick because the browser re-applies the inherited offset and
  // async data finishes settling the layout afterward, so we re-run on the next
  // paint and once more after a short delay. Mirrors scrollToAnalyzeTestResults.
  scrollToAddTestResultsTop() {
    const run = () => {
      const element = document.getElementById('add-test-results-panel');
      if (!element) {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        return;
      }
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top: Math.max(top, 0) });
    };
    run();
    requestAnimationFrame(run);
    setTimeout(run, 300);
  }

  toggleEnterTestResults() {
    this.setState({
      hideEnterTestResults: !this.state.hideEnterTestResults,
    });
  }

  render() {
    const {
      panelTitle,
      showUpgradeModal,
      showUploadPdfUpgradeModal,
      showTimeSavingFeaturesModal,
      hideEnterTestResults,
    } = this.state;
    const addTestResultsInfoTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about time saving features.
      </Tooltip>
    );
    const hideEnterTestResultsIconName = hideEnterTestResults
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <div>
        {this.renderAlerts()}

        <GenericModal
          show={showUpgradeModal}
          title="Maximum Number of Test Results Reached"
          text="You have reached the maximum number of test results allowed."
          link="https://labsmarts.com/pricing"
          linkText="Free user - click here to upgrade."
          additionalLink="https://labsmarts.com/my-account"
          additionalLinkText="Paid user - click here to upgrade"
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <GenericModal
          show={showUploadPdfUpgradeModal}
          title="Upload Test Results Feature Not Available"
          text="Upload Test Results feature is not available."
          link="https://labsmarts.com/upload_test_results_add_on"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUploadPdfUpgradeModal: false })}
        />

        <TimeSavingFeaturesModal
          show={showTimeSavingFeaturesModal}
          onHide={this.hasSeenTimeSavingFeaturesModal}
        />

        <Modal
          show={this.state.showMenstrualPhaseInfo}
          onHide={() => this.setState({ showMenstrualPhaseInfo: false })}
          size="lg"
          centered
        >
          <Modal.Header
            closeButton
            style={{ backgroundColor: '#337ab7', color: '#fff' }}
          >
            <Modal.Title>Menstrual Cycle Phases</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={menstrualCycleDiagram}
              alt="Diagram of the female menstrual cycle showing the follicular and luteal phases, ovulation, and menstruation"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Modal.Body>
        </Modal>

        <Card id="add-test-results-panel">
          <Card.Header onClick={() => this.toggleEnterTestResults()}>
            <FontAwesomeIcon icon={hideEnterTestResultsIconName} />
            &nbsp;&nbsp;{panelTitle}
            <OverlayTrigger overlay={addTestResultsInfoTooltip}>
              <FontAwesomeIcon
                icon="info-circle"
                className="float-right"
                onClick={this.showTimeSavingFeaturesModal}
              />
            </OverlayTrigger>
          </Card.Header>

          {hideEnterTestResults ? null : (
            <Card.Body>
              {this.renderErrors()}
              <br />
              <Form noValidate>
                {PanelField.GENERAL_FIELDS.map((field: any, index: number) => {
                  return field.isNotEditable ? null : this.formGroup(field);
                })}
                {this.props.currentUserProfile?.canSeeUploadTestResults() && (
                  <Upload
                    isUploadAvailable={this.props.currentUserProfile?.canUploadPdf()}
                    showModal={() => this.handleUploadPdfLimit()}
                    setState={obj => {
                      this.setState(prev => ({ ...prev, ...obj }));
                      setTimeout(() =>
                        Object.keys(obj).forEach(key =>
                          this.handleOnBlur({ target: { name: key } }),
                        ),
                      );
                    }}
                    clearForm={() => this.clearForm(true)}
                  />
                )}
                <Form.Group as={Row} controlId="bwUnitsGroup">
                  <Form.Label column sm={4} className="labsmarts-form-check-label">Units</Form.Label>
                  <Col sm={5}>
                    <Form.Check
                      className="form-check-inline-first"
                      inline
                      id="bw-units-us"
                      name="bwUnits"
                      label="US"
                      type="radio"
                      checked={this.state.bwUnits !== 'SI'}
                      onChange={() => {
                        const cm = this.state.height;
                        const { ft, inches } = cm != null ? MathUtils.cmToFtIn(Number(cm)) : { ft: '', inches: '' };
                        this.setState({
                          bwUnits: 'US',
                          heightFt: cm != null ? String(ft) : '',
                          heightIn: cm != null ? String(inches) : '',
                        });
                      }}
                    />
                    <Form.Check
                      inline
                      id="bw-units-si"
                      name="bwUnits"
                      label="SI"
                      type="radio"
                      checked={this.state.bwUnits === 'SI'}
                      onChange={() => {
                        const ft = Number(this.state.heightFt) || 0;
                        const inches = Number(this.state.heightIn) || 0;
                        const cm = (this.state.heightFt || this.state.heightIn)
                          ? MathUtils.ftInToCm(ft, inches)
                          : this.state.height;
                        this.setState({ bwUnits: 'SI', height: cm });
                      }}
                    />
                    &nbsp;
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="bw-units-tooltip">
                          Currently only applies to height and weight.
                          <br /><br />
                          SI units for markers will be available in a future update.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className="external-link"
                        icon="info-circle"
                        style={{ verticalAlign: 'middle', marginTop: '-2px', paddingBottom: '1px' }}
                      />
                    </OverlayTrigger>
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="heightEntryGroup">
                  <Form.Label column sm={4}>Height</Form.Label>
                  <Col sm={this.state.bwUnits === 'SI' ? 3 : 5}>
                    {this.state.bwUnits !== 'SI' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const ft = opt?.value ?? '';
                              const inches = Number(this.state.heightIn) || 0;
                              const cm = ft !== ''
                                ? MathUtils.ftInToCm(Number(ft), inches)
                                : (this.state.heightIn === '' ? undefined : this.state.height);
                              this.setState({ heightFt: ft, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[1,2,3,4,5,6,7,8].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightFt ?? '', label: this.state.heightFt || '—' }}
                          />
                        </div>
                        <span className="input-group-text">ft</span>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const inches = opt?.value ?? '';
                              const ft = Number(this.state.heightFt) || 0;
                              const cm = inches !== ''
                                ? MathUtils.ftInToCm(ft, Number(inches))
                                : (this.state.heightFt === '' ? undefined : this.state.height);
                              this.setState({ heightIn: inches, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[0,1,2,3,4,5,6,7,8,9,10,11].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightIn ?? '', label: this.state.heightIn !== '' && this.state.heightIn != null ? String(this.state.heightIn) : '—' }}
                          />
                        </div>
                        <span className="input-group-text">in</span>
                      </div>
                    ) : (
                      <InputGroup>
                        <Form.Control
                          maxLength={3}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const cm = e.target.value;
                            this.setState({ height: cm === '' ? undefined : Number(cm) });
                          }}
                          placeholder="e.g. 170"
                          size="sm"
                          style={{ maxWidth: '78px' }}
                          type="text"
                          value={this.state.height != null ? this.state.height : ''}
                        />
                        <InputGroup.Append>
                          <InputGroup.Text>cm</InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    )}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="weightEntryGroup">
                  <Form.Label column sm={4}>Weight</Form.Label>
                  <Col sm={3}>
                    <InputGroup>
                      <Form.Control
                        size="sm"
                        maxLength={3}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          if (val.trim() !== '' && Number(val) > 999) return;
                          if (this.state.bwUnits === 'SI') {
                            this.setState({ weight: val.trim() === '' ? '' : MathUtils.kgToLbs(Number(val)) });
                          } else {
                            this.setState({ weight: val });
                          }
                        }}
                        placeholder={this.state.bwUnits === 'SI' ? 'e.g. 68' : 'e.g. 150'}
                        style={{ maxWidth: '78px' }}
                        type="text"
                        value={
                          this.state.bwUnits === 'SI' && this.state.weight != null && this.state.weight !== ''
                            ? MathUtils.lbsToKg(Number(this.state.weight))
                            : (this.state.weight != null ? this.state.weight : '')
                        }
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{this.state.bwUnits === 'SI' ? 'kg' : 'lbs'}</InputGroup.Text>
                      </InputGroup.Append>
                    </InputGroup>
                  </Col>
                </Form.Group>
                {(() => {
                  const heightCm = this.state.height != null && this.state.height !== '' ? Number(this.state.height) : null;
                  const weightLbs = this.state.weight != null && this.state.weight !== '' ? Number(this.state.weight) : null;
                  if (heightCm == null || weightLbs == null) return null;
                  const bmi = MathUtils.bmi(weightLbs, heightCm);
                  return (
                    <Form.Group as={Row} controlId="bmiDisplayGroup">
                      <Form.Label column sm={4}>BMI</Form.Label>
                      <Col sm={3}>
                        <div style={{
                          border: '1px solid #ced4da',
                          borderRadius: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          fontSize: '14px',
                          display: 'inline-block',
                          minWidth: '80px',
                          backgroundColor: '#fff',
                          lineHeight: '1.5',
                        }}>
                          {bmi}
                        </div>
                      </Col>
                    </Form.Group>
                  );
                })()}
              </Form>
              <Card>
                <Card.Header>
                  Complete Blood Count (CBC) w/ Differentials &amp; Platelets
                </Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CBC_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Comprehensive Metabolic Panel (CMP)</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CMP_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Lipid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.LIPID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Iron Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.IRON_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Thyroid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.THYROID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Additional Markers</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.ADDITIONAL_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Hormones Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.SEX_HORMONE_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              {process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                this.renderImmuneInflammatoryCard()}
              {process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                this.renderMvxCard()}
              <Button
                id="submitButton"
                type="submit"
                onClick={this.handleSubmit}
                variant="primary"
              >
                Save
              </Button>
              &nbsp;&nbsp;
              <Button
                id="clearButton"
                type="button"
                variant="outline-primary"
                onClick={() => this.clearForm(true)}
              >
                Clear
              </Button>
            </Card.Body>
          )}
        </Card>

        {hideEnterTestResults ? null : (
          <button
            onClick={this.handleSubmit}
            title="Save Test Results"
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              height: '44px',
              padding: '0 20px',
              borderRadius: '22px',
              backgroundColor: '#766d66',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            Save
          </button>
        )}
      </div>
    );
  }
}
